import React, { useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { Text, TextType } from 'ui-components/components/text';

import createOrder from './create-an-order.svg';
import importCSV from './import-csv.svg';
import magnifyingGlass from './magnifying-glass.svg';
import connectStore from './store-connection.svg';
import { useOrderCSVImportContext } from '../../../../../components/order-import/order-import-context';
import { CREATE_DEMO_ORDER_BUTTON_CLICKED } from '../../../../../constants/analytics-events';
import { DEFAULT_DEMO_FULFILLMENT_PLAN_ID } from '../../../../../demos/demo-orders';
import { useCreateDemoOrder } from '../../../../../demos/hooks/use-create-demo-order';
import { MarketplaceIds } from '../../../../../enums/marketplace-ids';
import { useEnableNewOrderImport } from '../../../../../feature-flags/hooks/use-enable-new-order-import';
import { useOrderImportBasicFeatureGate } from '../../../../../feature-gates/use-order-import-basic-feature-gate';
import { useHasDemoOrdersFeature } from '../../../../../hooks/use-has-demo-orders-feature';
import { ORDERS_AWAITING_SHIPMENT_PAGE_URL } from '../../../../../routes/constants';
import { Segment } from '../../../../../services/analytics-services/segment';
import { LocalStorageService } from '../../../../../services/local-storage-service';
import { UserPermissionService } from '../../../../../services/user-permission-service';
import { openExistingDemoOrder } from '../../../../../store/demos/demos.actions';
import Global from '../../../../../store/global-selectors';
import { prepareToOpenNewOrderModal } from '../../../../../store/orders/edit-order/edit.order.actions';
import { getReferenceData } from '../../../../../store/reference-data/reference-data.actions';
import { refreshStoreList } from '../../../../../store/seller/seller.actions';
import { EventSourceStrings, Workflows } from '../../../../../tracking/tracking-constants';
import { useTrackWorkflow, WorkflowSteps } from '../../../../../tracking/use-workflow-tracking';
import MarketplaceSetupModal from '../../../../settings/stores/marketplace-connection-modal/setup';
import { CONNECTED_MARKETPLACES_GUIDS } from '../../../../settings/stores/marketplace-connection-modal/store-connection-success-notifications';
import SelectMarketplaceModal from '../../../../settings/stores/select-marketplace-modal';
import {
  useTrackMarketplaceConnection,
  sources as MarketplaceConnectionSources,
  stepNames,
} from '../../../../settings/stores/tracking-events';
import V2MarketplaceSetupModal from '../../../../settings/stores/v2-modals/marketplace-setup';
import { messages } from '../messages';
import style from '../style.module.less';

export const AwaitingShipmentPlaceholderActions = ({ isInDemoStore = false }) => {
  const intl = useIntl();
  const [setupMarketplace, setSetupMarketplace] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [marketplaceSelectorOpen, setMarketplaceSelectorOpen] = useState(false);
  const marketplacesReference = useSelector(Global.selectReferenceDatagetMarketplaces);
  const dispatch = useDispatch();
  const activeStores = useSelector(Global.selectSellerActiveStores);
  const trackMarketplaceConnection = useTrackMarketplaceConnection();
  const { isFeatureEnabled: hasOrderImportBasicFeature } = useOrderImportBasicFeatureGate();
  const showCSVImportTile = hasOrderImportBasicFeature && !isInDemoStore;
  const userRoles = useSelector(Global.selectUseruserRoles);
  const userHasConfigurationRole = UserPermissionService.userHasConfigurationRole(userRoles);
  const showConnectStoreTile = userHasConfigurationRole && !isInDemoStore;
  const { openCSVImport } = useOrderCSVImportContext();
  const existingDemoFulfillmentPlanId = useSelector(Global.selectSellerDemoFulfillmentPlanId);

  const showDemoOrderTile = useHasDemoOrdersFeature();
  const createDemoOrder = useCreateDemoOrder();

  const activeNonManualStores = useMemo(() => {
    return activeStores.filter((store) => store.marketplaceId !== MarketplaceIds.ShipStation);
  }, [activeStores]);
  const activeSellerStoreGuidList = useMemo(() => {
    return activeNonManualStores.map((store) => store.storeGuid);
  }, [activeNonManualStores]);

  const isNewOrderImportEnabled = useEnableNewOrderImport();
  const workflow = isNewOrderImportEnabled ? Workflows.CSV_ORDER_IMPORT_FLOW : Workflows.ORDER_IMPORT_FLOW;
  const trackOrderImport = useTrackWorkflow(workflow);
  const onClickImportCSV = useCallback(() => {
    trackOrderImport(WorkflowSteps.START, {
      source: EventSourceStrings.ORDERS_GRID_EMPTY_STATE_IMPORT_CSV,
    });
    openCSVImport();
  }, [openCSVImport, trackOrderImport]);

  const openNewOrderModal = useCallback(() => {
    dispatch(prepareToOpenNewOrderModal());
  }, [dispatch]);

  const onCloseSetupModal = useCallback(() => {
    trackMarketplaceConnection(WorkflowSteps.CANCEL, {
      selectedMarketplace: setupMarketplace,
      marketplaceName: setupMarketplace?.name,
      marketplaceId: setupMarketplace?.marketplaceId,
      stepName: stepNames.STORE_SPECIFIC_SETUP_MODAL,
    });
    setSetupMarketplace(null);
  }, [setupMarketplace, trackMarketplaceConnection]);

  const onCloseSelectMarketplaceModal = useCallback(() => {
    trackMarketplaceConnection(WorkflowSteps.CANCEL, {
      stepName: stepNames.SELECT_MARKETPLACE_MODAL,
    });
    setMarketplaceSelectorOpen(false);
  }, [trackMarketplaceConnection]);

  const onMarketplaceSelected = useCallback(
    (marketplace) => {
      setSetupMarketplace(marketplace);
      setMarketplaceSelectorOpen(false);
      trackMarketplaceConnection(WorkflowSteps.CLICK, {
        selectedMarketplace: marketplace,
        marketplaceName: marketplace?.name,
        marketplaceId: marketplace?.marketplaceId,
      });
    },
    [trackMarketplaceConnection],
  );

  const onRedirectToMarketplaceSetup = useCallback(() => {
    trackMarketplaceConnection(WorkflowSteps.REDIRECT, {
      selectedMarketplace: setupMarketplace,
      marketplaceName: setupMarketplace?.name,
      marketplaceId: setupMarketplace?.marketplaceId,
    });
  }, [setupMarketplace, trackMarketplaceConnection]);

  const onMarketplaceSetupBack = useCallback(() => {
    setSetupMarketplace(null);
    setMarketplaceSelectorOpen(true);
  }, [setMarketplaceSelectorOpen, setSetupMarketplace]);

  const onClickAddStore = useCallback(() => {
    setMarketplaceSelectorOpen(true);
    LocalStorageService.setItem(CONNECTED_MARKETPLACES_GUIDS, activeSellerStoreGuidList);
    trackMarketplaceConnection(WorkflowSteps.START, {
      source: MarketplaceConnectionSources.ORDERS_GRID_EMPTY_STATE,
    });
  }, [activeSellerStoreGuidList, trackMarketplaceConnection]);

  const onSaveNewMarketplace = useCallback(() => {
    const { marketplaceId, name } = setupMarketplace;
    if (!marketplacesReference.some((market) => market.marketplaceId === marketplaceId)) {
      dispatch(getReferenceData());
    }
    dispatch(refreshStoreList());
    trackMarketplaceConnection(WorkflowSteps.COMPLETE, {
      selectedMarketplace: setupMarketplace,
      marketplaceName: name,
      marketplaceId,
    });
    LocalStorageService.clearItemsWithPrefix(CONNECTED_MARKETPLACES_GUIDS);
    setSetupMarketplace(null);
  }, [setupMarketplace, marketplacesReference, dispatch, trackMarketplaceConnection]);

  const onSetupError = useCallback(
    (error) => {
      const errorMessage = error?.error?.ExceptionMessage || error?.message;
      trackMarketplaceConnection(WorkflowSteps.ERROR, {
        error,
        errorMessage,
      });
    },
    [trackMarketplaceConnection],
  );

  const sellerId = useSelector(Global.selectSellerSellerId);

  const onDemoOrderClick = () => {
    const isExistingOrder = existingDemoFulfillmentPlanId !== DEFAULT_DEMO_FULFILLMENT_PLAN_ID;

    Segment.trackEvent(CREATE_DEMO_ORDER_BUTTON_CLICKED, {
      source: EventSourceStrings.ORDERS_GRID_EMPTY_STATE_DEMO_ORDER,
      isExistingOrder,
      sellerId,
    });

    if (isExistingOrder) {
      dispatch(openExistingDemoOrder());
    } else {
      setIsLoading(true);
      createDemoOrder();
    }
  };

  const isFourButtonView = showDemoOrderTile && showCSVImportTile;

  return (
    <>
      <div
        className={classNames(style.buttons, {
          [style['four-buttons']]: isFourButtonView,
        })}
      >
        {showConnectStoreTile ? (
          <Button className={style['large-button']} onClick={onClickAddStore} theme={THEME_CONSTANTS.unstyled}>
            <img alt={intl.formatMessage(messages.connectStoreButton)} className={style['button-images']} src={connectStore} />
            <Text className={style['button-label']} textType={TextType.H4} value={messages.connectStoreButton} />
          </Button>
        ) : null}
        {!isInDemoStore && (
          <Button className={style['large-button']} onClick={openNewOrderModal} theme={THEME_CONSTANTS.unstyled}>
            <img alt={intl.formatMessage(messages.createOrderButton)} className={style['button-images']} src={createOrder} />
            <Text className={style['button-label']} textType={TextType.H4} value={messages.createOrderButton} />
          </Button>
        )}

        {showDemoOrderTile ? (
          <Button
            className={style['large-button']}
            disabled={isLoading}
            isLoading={isLoading}
            onClick={onDemoOrderClick}
            theme={THEME_CONSTANTS.unstyled}
          >
            {!isLoading && (
              <>
                <img alt={intl.formatMessage(messages.createOrderButton)} className={style['button-images']} src={magnifyingGlass} />
                <Text
                  className={style['button-label']}
                  textType={TextType.H4}
                  value={existingDemoFulfillmentPlanId > 0 ? messages.openDemoOrder : messages.takeATour}
                />
              </>
            )}
          </Button>
        ) : null}
        {showCSVImportTile ? (
          <Button className={style['large-button']} onClick={onClickImportCSV} theme={THEME_CONSTANTS.unstyled}>
            <img alt={intl.formatMessage(messages.importCSV)} className={style['button-images']} src={importCSV} />
            <Text className={style['button-label']} textType={TextType.H4} value={messages.importCSV} />
          </Button>
        ) : null}
      </div>
      {marketplaceSelectorOpen ? (
        <SelectMarketplaceModal onClose={onCloseSelectMarketplaceModal} onMarketplaceSelected={onMarketplaceSelected} />
      ) : null}
      {setupMarketplace && !setupMarketplace.isSupported ? (
        <V2MarketplaceSetupModal marketplaceId={setupMarketplace.marketplaceId} onClose={onCloseSetupModal} onSave={onSaveNewMarketplace} />
      ) : null}
      {setupMarketplace && setupMarketplace.isSupported ? (
        <MarketplaceSetupModal
          marketplaceApiCode={setupMarketplace.apiCode}
          onBack={onMarketplaceSetupBack}
          onClose={onCloseSetupModal}
          onError={onSetupError}
          onRedirect={onRedirectToMarketplaceSetup}
          onSave={onSaveNewMarketplace}
          redirectPath={ORDERS_AWAITING_SHIPMENT_PAGE_URL}
        />
      ) : null}
    </>
  );
};
